/**
 * @file EstadoCard.js
 * @description Tarjeta de resumen de vehículos por estado geográfico
 * @module components/cards/EstadoCard
 *
 * Características principales:
 * - Vista resumen de vehículos RFID en un estado (Yucatán, Campeche, etc.)
 * - Estadísticas principales: total de vehículos y sitios
 * - Desglose visual por marca con colores identificadores
 * - Logos de marcas cuando están disponibles
 * - Estados vacíos con diseño diferenciado
 * - Colores temáticos por estado (azul para Yucatán, verde para Campeche, etc.)
 * - Clickable para navegar a vista de sitios del estado
 * - Imagen de fondo de monumento landmark por estado (sutil, peek desde la derecha)
 *
 * Jerarquía de navegación:
 * Estados (este componente) → Sitios → Subsitios → Detalle
 */

import React, { useState, useMemo, useCallback } from 'react';
import { MapPin, Car, Building2, Package } from 'lucide-react';
import estadosImages from '../../data/estadosImages';

const BRANDS_PER_PAGE = 4;

const marcasLogos = {
  'FORD': '/logos/ford.png',
  'LINCOLN': '/logos/lincoln.png',
  'GWM': '/logos/gwm.png',
  'MAZDA': '/logos/mazda.png',
  'JEEP': '/logos/jeep.svg',
  'RAM': '/logos/ram.svg',
  'PEUGEOT': '/logos/peugeot.svg',
  'FIAT': '/logos/fiat.svg',
  'KIA': '/logos/kia.svg',
  'TOYOTA': '/logos/toyota.svg',
  'MITSUBISHI': '/logos/mitsubishi.svg',
  'HYUNDAI': '/logos/hyundai.svg',
  'BYD': '/logos/byd.svg',
  'BMW': '/logos/bmw.svg',
  'MINI': '/logos/mini.svg',
};

const EstadoCard = ({ estado, onEstadoClick }) => {
  const estaVacio = estado.totalVehiculos === 0;

  // Resolve background landmark image for this state (null if not configured)
  const bgEntry = estadosImages[estado.nombre?.toUpperCase()] ?? null;
  const bgImage = bgEntry?.src ?? null;
  const bgFlip  = bgEntry?.flip ?? false;
  const [bgError, setBgError] = useState(false);
  const showBgImage = bgImage && !bgError;

  // Carousel de marcas
  const sortedMarcas = useMemo(() =>
    Object.entries(estado.marcas || {}).sort((a, b) => b[1] - a[1]),
    [estado.marcas]
  );
  const totalPages = Math.ceil(sortedMarcas.length / BRANDS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(0);
  const prevPage = useCallback((e) => { e.stopPropagation(); setCurrentPage(p => (p - 1 + totalPages) % totalPages); }, [totalPages]);
  const nextPage = useCallback((e) => { e.stopPropagation(); setCurrentPage(p => (p + 1) % totalPages); }, [totalPages]);

  const getEstadoBgColor = (nombreEstado) => {
    const colorMap = {
      'YUCATAN': 'bg-blue-50',
      'YUCATÁN': 'bg-blue-50',
      'CAMPECHE': 'bg-green-50',
      'QUINTANA ROO': 'bg-purple-50'
    };
    return colorMap[nombreEstado?.toUpperCase()] || 'from-gray-50 to-gray-100';
  };

  const getEstadoBadgeColor = (nombreEstado) => {
    const colorMap = {
      'YUCATAN': 'bg-[#1d2e61] text-white',
      'YUCATÁN': 'bg-[#1d2e61] text-white',
      'CAMPECHE': 'bg-green-600 text-white',
      'QUINTANA ROO': 'bg-purple-600 text-white'
    };
    return colorMap[nombreEstado?.toUpperCase()] || 'bg-gray-600 text-white';
  };

  return (
    <div
      className={`relative overflow-hidden border rounded-lg p-3 sm:p-4 transition-all duration-200 cursor-pointer sm:hover:scale-[1.02] transform ${estaVacio
          ? 'bg-gray-50 border-gray-200 hover:shadow-md hover:bg-gray-100'
          : `bg-white border-gray-300 hover:shadow-lg hover:${getEstadoBgColor(estado.nombre)}`
        } flex flex-col h-full`}
      onClick={() => onEstadoClick && onEstadoClick(estado)}
    >
      {/* ===== BACKGROUND LANDMARK IMAGE ===== */}
      {showBgImage && (
        <>
          {/* Layer 1: landmark photo at low opacity */}
          <img
            src={bgImage}
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-right-top opacity-70 pointer-events-none z-0"
            style={bgFlip ? { transform: 'scaleX(-1)' } : undefined}
            onError={() => setBgError(true)}
          />
          {/* Layer 2: gradient overlay — white on left, transparent on right */}
          <div
            className="absolute inset-0 pointer-events-none z-[1]"
            style={{ background: 'linear-gradient(to right, white 25%, rgba(255,255,255,0.6) 45%, rgba(255,255,255,0) 100%)' }}
          />
        </>
      )}

      {/* All card content sits above image layers */}
      <div className="relative z-10 flex flex-col h-full">

        {/* ===== HEADER DEL ESTADO ===== */}
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <div className="flex items-center space-x-2 min-w-0">
            <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 shrink-0" />
            <h3 className="font-bold text-gray-900 text-base sm:text-lg truncate">{estado.nombre}</h3>
          </div>
          <span className={`text-xs sm:text-sm px-2 sm:px-3 py-0.5 sm:py-1 rounded-full font-medium shrink-0 ml-1 ${estaVacio
              ? `bg-gray-500 text-white`
              : `${getEstadoBadgeColor(estado.nombre)}`
            }`}>
            Estado
          </span>
        </div>

        {/* ===== ESTADÍSTICAS PRINCIPALES ===== */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-white/70 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-1">
                <Car className="w-4 h-4 text-gray-600" />
                <span className="text-xs text-gray-600">Vehículos</span>
              </div>
              <span className="text-lg font-bold text-gray-900">{estado.totalVehiculos}</span>
            </div>
          </div>
          <div className="bg-white/70 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-1">
                <Building2 className="w-4 h-4 text-gray-600" />
                <span className="text-xs text-gray-600">Sitios</span>
              </div>
              <span className="text-lg font-bold text-gray-900">{estado.totalSitios}</span>
            </div>
          </div>
        </div>

        {/* ===== DESGLOSE POR MARCAS ===== */}
        {estaVacio ? (
          <div className="space-y-3 mb-3">
            <div className="flex items-center justify-center py-4 border-2 border-dashed border-gray-300 rounded-lg">
              <div className="text-center">
                <Package className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500 font-medium">Sin vehículos</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-3">
            <p className="text-xs font-medium text-gray-700 mb-2">Vehículos por marca:</p>

            {/* Carousel de marcas */}
            <div className="overflow-hidden">
              <div
                style={{
                  display: 'flex',
                  transform: `translateX(-${currentPage * 100}%)`,
                  transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                {Array.from({ length: totalPages }).map((_, pageIdx) => {
                  const pageBrands = sortedMarcas.slice(
                    pageIdx * BRANDS_PER_PAGE,
                    (pageIdx + 1) * BRANDS_PER_PAGE
                  );
                  return (
                    <div key={pageIdx} style={{ minWidth: '100%' }}>
                      {pageBrands.map(([marca, cantidad]) => (
                        <div key={marca} className="flex items-center justify-between bg-white/50 rounded px-2 py-1 mb-1">
                          <div className="flex items-center space-x-2">
                            {marcasLogos[marca] && (
                              <img
                                src={marcasLogos[marca]}
                                alt={`${marca} logo`}
                                className="w-[18px] h-[18px] object-contain"
                                onError={(e) => e.target.style.display = 'none'}
                              />
                            )}
                            <span className="text-sm text-gray-700">{marca}</span>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">{cantidad}</span>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Paginación — solo si hay más de una página */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-1.5" onClick={e => e.stopPropagation()}>
                <button onClick={prevPage} aria-label="Página anterior"
                  className="w-5 h-5 flex items-center justify-center rounded text-[#1d2e61] hover:bg-[#1d2e61]/10 transition-all">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <span className="text-[10px] font-medium text-gray-500 tabular-nums">
                  {currentPage + 1}/{totalPages}
                </span>
                <button onClick={nextPage} aria-label="Página siguiente"
                  className="w-5 h-5 flex items-center justify-center rounded text-[#1d2e61] hover:bg-[#1d2e61]/10 transition-all">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}

        {/* ===== INDICADOR DE ACCIÓN ===== */}
        <div className="pt-3 border-t border-gray-200 mt-auto">
          <p className="text-xs text-gray-600 text-center flex items-center justify-center space-x-1">
            <span>Ver sitios de {estado.nombre}</span>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </p>
        </div>

      </div>{/* end relative z-10 */}
    </div>
  );
};

export default EstadoCard;
